import { JavaScriptVariableType } from '@unifyapps/defs/types/javascriptVariable';
import _isNil from 'lodash/isNil';

/**
 * Converts strings to JavaScript objects/arrays/values.
 * Handles valid JSON, JS-style objects (single quotes, unquoted keys), and type-specific conversions.
 * Returns the original string if parsing fails.
 *
 * Examples:
 * - "{'name':'John'}" -> {name: 'John'}
 * - "{'name':}" -> "{'name':}" (invalid object kept as string)
 * - "[1,2,3]" -> [1,2,3]
 * - "[1,2,]" -> "[1,2,]" (invalid array kept as string)
 *
 * @param input - The input string that might contain a JSON-like object
 * @param type - The expected JavaScript variable type
 * @returns Parsed object/array/value or the original string
 */
export function smartConvertStringToObject(input: string, type: JavaScriptVariableType): unknown {
  // If input is not a string or is empty, return it unchanged
  if (typeof input !== 'string' || !input.trim()) {
    return input;
  }

  const trimmed = input.trim();

  // Try parsing as valid JSON first
  try {
    return JSON.parse(trimmed);
  } catch {
    // JSON.parse failed, continue to handle JS-style objects/arrays
  }

  // For Object/Array types, try converting JS-style syntax (single quotes, unquoted keys)
  if (type === JavaScriptVariableType.Object || type === JavaScriptVariableType.Array) {
    try {
      // Convert single quotes to double quotes (valid JS but not valid JSON)
      let jsonReady = trimmed.replace(/'/g, '"');

      // Handle unquoted property names (word characters followed by colon)
      jsonReady = jsonReady.replace(/\b\w+:/g, (match) => {
        const propName = match.slice(0, -1); // Remove the colon
        return `"${propName}":`;
      });

      return JSON.parse(jsonReady);
    } catch {
      // Conversion failed, return original input
      return input;
    }
  }

  // Handle other types
  switch (type) {
    case JavaScriptVariableType.Boolean:
      try {
        return JSON.parse(trimmed);
      } catch {
        return input;
      }
    case JavaScriptVariableType.Number:
      return !_isNil(input) ? Number(input) : input;
    case JavaScriptVariableType.String:
    default:
      return input;
  }
}
